import type { Language, RowFindings } from '@eformulieren/engine'
import { RESPONSE_SHAPE, type Field, type Finding, type VerwijzingOutcome } from '@eformulieren/model'
import type { CommentRange } from '../document/commentRange.js'
import { type ReactNode, useEffect, useState } from 'react'
import * as Y from 'yjs'
import {
  type Origin,
  recordOrigin,
  createRowFragment,
  createRowText,
  fieldText,
  isMergedText,
  recordOriginIn,
  originIn,
  richtextFragment,
  rowFragment,
  rowText,
  rowsList,
  valuesMap,
  setDocuments,
  setRowValueIn,
  setValue,
  setValues,
} from '../document/sectionDocument.js'
import { useOriginIn, useRowValue } from '../document/useSectionDocument.js'
import { consentMap, consentsIn } from '../document/consents.js'
import { FieldShell } from '../field/FieldShell.js'
import type { ControlProps, Shared, Stamp, UploadOutcome } from '../field/controls/kinds.js'
import { controlFor, labelViaHeader, takesInput } from '../field/registry.js'
import { seriesRolesFor } from './seriesRoles.js'

/**
 * De binding van een veld en een rijcel aan het sectiedocument: wat een control leest, schrijft en
 * toont (R-09, R-19, R-50, R-89, R-97, R-140). Tot deze opsplitsing stond dit onderaan
 * `FillForm.tsx`, dat het per veld aanroept.
 */

/** Wat een rich-text-veld of -kolom met citaties kan (R-50). */
export interface CitationProps {
  chooseReference: () => Promise<readonly string[] | undefined>
  newCitationId: () => string
  citationLabels?: ReadonlyMap<string, string>
  citationEntries?: ReadonlyMap<string, readonly string[]>
  sourcePanel: ReactNode
}

/** De binding per antwoordvorm; de vorm komt uit het model en niet uit een lijst hier. */
export function bind({
  gedeeld: shared,
  doc,
  write,
  eisen: requirements,
  upload,
  aantallen: counts,
  taal: language,
  identiteit: identity,
  opmerkingen: comments,
  citatie: citation,
  rijCitatie: rowCitation,
  verwijzingen,
  rijbevindingen: rowFindings,
  verplichteVelden: requiredFieldIds,
  toonAlleMeldingen: showAllNotices,
  anchorFor: anchorOf,
  akkoordDoorIndiener: byIndiener,
  akkoord: consentAction,
}: {
  gedeeld: Shared
  doc: Y.Doc
  /**
   * De bevindingen per rijveld en per rij, uit `evaluate` (R-140, R-41). Zonder deze kreeg een cel
   * nooit een melding: de status is op veld-id gesleuteld en zegt niet in welke rij een regel
   * faalt.
   */
  rijbevindingen?: Readonly<Record<string, Readonly<Record<string, RowFindings>>>>
  /**
   * De velden met een harde `verplicht`-regel (`requiredFields` uit de engine), rijvelden erbij.
   * Zonder deze kreeg een cel nooit de verplicht-aanduiding (R-09), ook niet als er een harde regel
   * op het rijveld staat; het gewone veld kreeg hem wel.
   */
  verplichteVelden?: ReadonlySet<string>
  /** Alle meldingen tonen, ook in een cel die niemand heeft aangeraakt (zie `FieldShell`). */
  toonAlleMeldingen?: boolean
  /** Het anker van een cel, voor het overzicht van wat er nog openstaat. */
  anchorFor?: (fieldId: string, rowId?: string) => string
  /**
   * De akkoordvelden die deze lezer niet zelf geeft, ook als kolom van een herhaalgroep (besluit van
   * 3 november 2026, `foreignConsents` in `FillForm`): die tekent het akkoord als leesweergave met
   * "Dit akkoord geeft de indiener".
   */
  akkoordDoorIndiener?: ReadonlySet<string>
  /**
   * Een akkoord per persoon geven of intrekken langs de pod (eisenboek 12.56). Zonder deze functie kan de
   * lezer het akkoord alleen zien, zoals in een stand uit de geschiedenis of na een verlopen sessie.
   */
  akkoord?: ((handeling: 'geven' | 'intrekken') => Promise<void>) | undefined
  /** Voor een subgrid in een herhaalgroeprij; zie de tak in `RowCell`. */
  verwijzingen?: Readonly<Record<string, VerwijzingOutcome>>
  write: (action: (origin: Origin) => void) => void
  eisen: { typen: readonly string[]; maximumAantal: number; maximumGrootteMb: number } | undefined
  /** Het bestand aanleveren bij de opslag, voor een bijlageveld (R-104). */
  upload?: (file: File) => Promise<UploadOutcome>
  aantallen: { minimum?: number; maximum?: number } | undefined
  taal: Language
  /** Wie er leest, uit het token (`FillFormProps.identiteit`): voor "door u" op een akkoord. */
  identiteit: string
  /**
   * Wat een rich-text-veld met opmerkingen kan (R-73, R-72), en de cursors van medeschrijvers, of niets.
   *
   * Alleen voor rich text, want het anker is een mark in de tekst; een keuzelijst heeft geen
   * plek waar een opmerking aan hangt. Een schrijver krijgt `onNieuweOpmerking`, een meelezer
   * `onOpmerkingLangsPod`, wie geen opmerking kan plaatsen geen van beide.
   */
  opmerkingen?:
    | {
        nieuwDraadId?: () => string
        onNieuweOpmerking?: (
          thread: { id: string; veldId: string; citaat: string },
          text: string,
        ) => void
        onOpmerkingLangsPod?: (bereik: CommentRange, text: string) => Promise<{ readonly draad: string }>
        presence?: { provider: unknown; naam: string; anoniem?: boolean; nameOf?: (person: string) => string }
      }
    | undefined
  /** Wat een rich-text-veld met citaties kan (R-50), of niets. Zie `gebruikBronKiezer`. */
  citatie?: CitationProps | undefined
  /**
   * Hetzelfde voor een rich-text-kolom in een herhaalgroeprij (R-50): per cel een eigen paneel, want
   * de kiezer toont zijn paneel bij het veld dat erom vroeg, en een kolom heeft er één per rij.
   */
  rijCitatie?: ((rowFieldId: string, rowId: string) => CitationProps) | undefined
}): ControlProps {
  const field = shared.veld

  if (field.control === 'richtext') {
    return {
      ...shared,
      vorm: 'fragment',
      fragment: richtextFragment(doc, field.id),
      onOwnChange: () => write((origin) => recordOrigin(doc, field.id, origin)),
      ...(comments ?? {}),
      ...(citation ?? {}),
    }
  }

  if (isMergedText(field.control)) {
    return {
      ...shared,
      vorm: 'tekst',
      tekst: fieldText(doc, field.id),
      // Binnen de transactie van de wijziging (`useTextBinding`), dus tekst en herkomst samen.
      onOwnChange: () => write((origin) => recordOrigin(doc, field.id, origin)),
    }
  }

  if (field.control === 'herhaalgroep') {
    return {
      ...shared,
      vorm: 'rijen',
      lijst: rowsList(doc, field.id),
      renderRowField: (rowField, row, rowId) => {
        const own = rowFindings?.[rowField.id]?.[rowId]
        return (
          <RowCell
            rowField={rowField}
            row={row}
            taal={language}
            bewerkbaar={shared.bewerkbaar && byIndiener?.has(rowField.id) !== true}
            doorIndiener={byIndiener?.has(rowField.id) === true}
            write={write}
            hardeControles={byIndiener?.has(rowField.id) === true ? NO : (own?.hardeControles ?? NO)}
            waarschuwingen={own?.waarschuwingen ?? NO}
            verplicht={byIndiener?.has(rowField.id) !== true && (requiredFieldIds?.has(rowField.id) ?? false)}
            showAllNotices={showAllNotices ?? false}
            identiteit={identity}
            {...(rowCitation === undefined || rowField.control !== 'richtext' ? {} : { citatie: rowCitation(rowField.id, rowId) })}
            {...(anchorOf === undefined ? {} : { anchor: anchorOf(rowField.id, rowId) })}
            {...(verwijzingen === undefined ? {} : { verwijzingen })}
          />
        )
      },
      ...(counts === undefined ? {} : { counts }),
    }
  }

  if (field.control === 'akkoordPerPersoon') {
    // Uit de map van het veld, door de pod gezet (`consents.ts`); de naam is het e-mailadres uit het token.
    const { akkoorden: stamps, stand } = consentsIn(consentMap(doc, field.id))
    const lines = stamps.map((stamp) => ({
      sleutel: stamp.persoon,
      naam: stamp.email,
      eigen: stamp.persoon === identity,
      gegevenOp: stamp.gegevenOp,
      geldig: stand !== undefined && stamp.stand === stand,
      namens: stamp.namens !== undefined,
    }))
    const own = lines.find((line) => line.eigen)
    return {
      ...shared,
      vorm: 'akkoorden',
      akkoorden: lines,
      ...(own === undefined ? {} : { eigen: { geldig: own.geldig } }),
      magGeven: shared.bewerkbaar && consentAction !== undefined,
      ...(consentAction === undefined ? {} : { onGeven: async () => await consentAction('geven'), onIntrekken: async () => await consentAction('intrekken') }),
    }
  }

  const current = valuesMap(doc).get(field.id)

  switch (RESPONSE_SHAPE[field.control]) {
    case 'documenten':
      return {
        ...shared,
        vorm: 'documenten',
        documenten: asList(current),
        onDocuments: (nextDocuments) => write((origin) => setDocuments(doc, field.id, nextDocuments, origin)),
        ...(requirements === undefined ? {} : { requirements }),
        ...(upload === undefined ? {} : { upload }),
      }
    case 'waarden':
      return {
        ...shared,
        vorm: 'waarden',
        waarden: asList(current),
        onValues: (nextDocuments) => write((origin) => setValues(doc, field.id, nextDocuments, origin)),
      }
    default: {
      const stamp = consentStamp(field, current, originIn(valuesMap(doc), field.id), identity)
      return {
        ...shared,
        vorm: 'waarde',
        waarde: asSingleValue(current),
        onWaarde: (nextValue) => write((origin) => setValue(doc, field.id, nextValue ?? null, origin, momentOf(field))),
        ...(stamp === undefined ? {} : { stempel: stamp }),
        ...(byIndiener?.has(field.id) === true ? { doorIndiener: true } : {}),
      }
    }
  }
}

/** Eén lege lijst, zodat een cel zonder bevindingen niet elke render een nieuwe krijgt. */
const NO: readonly never[] = []

function asList<T>(value: unknown): readonly T[] {
  return Array.isArray(value) ? (value as T[]) : []
}

function asSingleValue(value: unknown): string | number | boolean | null | undefined {
  if (value === undefined || value === null) return value
  const kind = typeof value
  if (kind === 'string' || kind === 'number' || kind === 'boolean') {
    return value as string | number | boolean
  }
  return undefined
}

/**
 * De herkomst van een rich-text-cel in een rij, met dezelfde sleutels als elders.
 *
 * De tekst staat als fragment in de rijmap; de herkomst staat ernaast, want ook een
 * cel is een antwoord met een auteur en een moment (R-97).
 */
/**
 * De stempel op een akkoordveld (R-19, R-89): de herkomst van het veld, zolang het op Ja staat.
 *
 * Geen eigen sleutels in het document, en dat is de keuze. Wie het akkoord gaf en wanneer is
 * precies wie het veld op Ja zette en wanneer, en dat staat al in `vld-x@door` en `vld-x@op`, door
 * de pod uit het token gezet (`apps/runtime/src/origin.ts`) en in de revisie meegenomen bij het
 * antwoord (R-97). Een tweede paar sleutels zou bytes toevoegen aan een document dat ze nooit meer
 * kwijtraakt (valkuil 1) en een oudere client iets laten zien dat hij niet kent (valkuil 4). Bij Nee
 * blijft de herkomst staan, want die zegt wie Nee koos, maar een stempel is er dan niet: daarom
 * hangt deze functie aan de waarde en niet alleen aan de herkomst.
 *
 * **De persoon gaat niet mee, alleen of het de lezer zelf was.** Dit product kent geen naam
 * (R-155e), en de persoonsaanduiding is een sleutel die niet op het scherm hoort. De revisie houdt
 * wel wie het was; alleen de weergave zegt "u" of "een andere invuller". Een echte naam is een open
 * vraag aan NWO (persoonsgegevens en de inrichting bij de identiteitsprovider).
 */
function consentStamp(field: Field, value: unknown, origin: Origin | undefined, identity: string): Stamp | undefined {
  if (field.control !== 'toggle' || field.akkoord !== true) return undefined
  if (value !== true || origin === undefined) return undefined
  return { eigen: origin.door === identity, moment: origin.gewijzigdOp }
}

/**
 * Een akkoord schrijft zijn moment bij elke keuze; elk ander veld alleen bij een nieuwe auteur
 * (besluit van 29 oktober 2026, ontwerp 4.6). Zie `WriteOptions` in `sectionDocument.ts`.
 */
function momentOf(field: { readonly control: string; readonly akkoord?: boolean | undefined }): { exactMoment: boolean } {
  return { exactMoment: field.control === 'toggle' && field.akkoord === true }
}

function setRowOrigin(row: Y.Map<unknown>, fieldId: string, origin: Origin): void {
  row.doc?.transact(() => recordOriginIn(row, fieldId, origin))
}

/** Eén cel in een rij: eigen abonnement, eigen schrijfactie. */
function RowCell({
  rowField,
  row,
  taal: language,
  bewerkbaar: editable,
  write,
  verwijzingen,
  hardeControles: hardChecks,
  waarschuwingen: warnings,
  verplicht: required,
  showAllNotices,
  anchor,
  identiteit: identity,
  citatie: citation,
  doorIndiener: byIndiener,
}: {
  rowField: Field
  /** Een akkoordkolom die de indiener geeft en niet deze lezer (`akkoordDoorIndiener`). */
  doorIndiener: boolean
  /** Wat deze cel met citaties kan, als ze een rich-text-kolom is (R-50). */
  citatie?: CitationProps
  row: Y.Map<unknown>
  taal: Language
  bewerkbaar: boolean
  write: (action: (origin: Origin) => void) => void
  /** De bevindingen van dit veld in deze rij (R-140), uit `Evaluatie.rijen`. */
  hardeControles: readonly Finding[]
  waarschuwingen: readonly Finding[]
  /** Of er een harde `verplicht`-regel op dit rijveld staat (R-09), net als bij een gewoon veld. */
  verplicht: boolean
  showAllNotices: boolean
  anchor?: string
  /** Wie er leest, voor "door u" op een akkoord in een rij (R-19). */
  identiteit: string
  /** Voor een subgrid in een rij, die uit het dossier leest en niet uit de rij. */
  verwijzingen?: Readonly<Record<string, VerwijzingOutcome>>
}): ReactNode {
  const value = useRowValue(row, rowField.id)
  // Een eigen abonnement op de herkomst, want `useRowValue` hertekent alleen bij de waarde, en de
  // pod zet de herkomst na het aanvinken nog recht (`apps/runtime/src/origin.ts`).
  const origin = useOriginIn(row, rowField.id)
  const stamp = consentStamp(rowField, value, origin, identity)
  const draw = controlFor(rowField.control)

  /**
   * Een rich-text-kolom hangt aan een fragment in de rijmap. Normaal legt
   * `addRowTo` het aan; is het er niet (een rij van een oudere client), dan
   * gebeurt dat hier in een effect en niet tijdens het renderen: een document mag
   * niet veranderen doordat iemand ernaar kijkt.
   */
  const isRichText = rowField.control === 'richtext'
  const [fragment, setFragment] = useState(() => (isRichText ? rowFragment(row, rowField.id) : undefined))
  useEffect(() => {
    if (!isRichText) return
    const existing = rowFragment(row, rowField.id)
    setFragment(existing ?? createRowFragment(row, rowField.id))
  }, [isRichText, row, rowField.id])

  /**
   * Een tekstkolom hangt aan een `Y.Text` in de rijmap (R-72), met dezelfde terugval als het
   * fragment hierboven. `addRowTo` en het zaaien leggen hem aan; het effect is er alleen voor een
   * rij die zonder deze kolom als tekst is gemaakt.
   */
  const isText = isMergedText(rowField.control)
  const [text, setCellText] = useState(() => (isText ? rowText(row, rowField.id) : undefined))
  useEffect(() => {
    if (!isText) return
    const existing = rowText(row, rowField.id)
    setCellText(existing ?? createRowText(row, rowField.id))
  }, [isText, row, rowField.id])

  if (draw === undefined) return null

  return (
    <FieldShell
      veld={rowField}
      nummer={undefined}
      taal={language}
      verplicht={required}
      sterretje={editable && takesInput(rowField.control)}
      hardeControles={hardChecks}
      waarschuwingen={warnings}
      showAllNotices={showAllNotices}
      labelViaHeader={labelViaHeader(rowField.control)}
      labelInControl={'akkoord' in rowField && rowField.akkoord === true}
      empty={rowValueEmpty(value)}
      {...(anchor === undefined ? {} : { anchor })}
    >
      {(link) => {
        const shared: Shared = { veld: rowField, taal: language, koppeling: link, bewerkbaar: editable }
        if (isRichText) {
          // Het fragment komt in het effect hierboven; tot dan is er niets te tekenen.
          return fragment === undefined
            ? null
            : draw({
                ...shared,
                vorm: 'fragment',
                fragment,
                onOwnChange: () =>
                  write((origin) => setRowOrigin(row, rowField.id, origin)),
                ...(citation ?? {}),
              })
        }
        if (isText) {
          return text === undefined
            ? null
            : draw({
                ...shared,
                vorm: 'tekst',
                tekst: text,
                onOwnChange: () => write((origin) => setRowOrigin(row, rowField.id, origin)),
              })
        }
        /**
         * Een subgrid als rijveld leest uit het dossier en niet uit de rij.
         *
         * `RijVeld` in het model staat een subgrid in een rij toe, en sinds M6.4 vraagt die
         * control de verwijzingsvorm. Zonder deze tak kreeg hij `vorm: 'waarde'` en gooide
         * `requireVerwijzing` tijdens de render; er is nergens een error boundary, dus React
         * ontkoppelde de hele boom en de invuller zag een wit scherm in plaats van zijn
         * formulier. Een leesronde vond het; vóór M6.4 tekende dezelfde definitie nog een
         * tekstregel. Dat is de duurste soort regressie: het formulier haalt de poort, en het
         * valt pas om bij de invuller.
         *
         * De uitkomst komt uit dezelfde map als bij een gewoon veld. Een reeks per rij bestaat
         * niet, dus in de praktijk is dat `onbeschikbaar` en toont de control de melding; dat
         * is wat R-61 vraagt en het is in elk geval geen wit scherm.
         */
        if (rowField.control === 'subgrid') {
          const fromSource = 'bron' in rowField ? rowField.bron : undefined
          const roles = seriesRolesFor(fromSource)
          return draw({
            ...shared,
            vorm: 'verwijzing',
            uitkomst: verwijzingen?.[rowField.id] ?? { stand: 'onbeschikbaar' },
            ...(fromSource === undefined || fromSource === null ? {} : { bron: fromSource.contract }),
            ...(roles === undefined ? {} : { roles }),
          })
        }

        // Een rijveld is verder altijd een eenvoudige, toggle- of keuzevorm (`RijVeld` in het
        // model), dus één waarde of een lijst keuzes.
        if (RESPONSE_SHAPE[rowField.control] === 'waarden') {
          return draw({
            ...shared,
            vorm: 'waarden',
            waarden: asList(value),
            onValues: (nextDocuments) =>
              write((origin) => setRowValueIn(row, rowField.id, [...nextDocuments], origin)),
          })
        }
        return draw({
          ...shared,
          vorm: 'waarde',
          waarde: asSingleValue(value),
          onWaarde: (nextValue) =>
            write((origin) => setRowValueIn(row, rowField.id, nextValue ?? null, origin, momentOf(rowField))),
          ...(stamp === undefined ? {} : { stempel: stamp }),
          ...(byIndiener ? { doorIndiener: true } : {}),
        })
      }}
    </FieldShell>
  )
}

/**
 * Of een waarde in een rij leeg is, voor het moment van de melding (R-25). De rij draagt de waarde
 * zoals Yjs haar heeft, en niet als antwoord; de evaluatie leest dezelfde waarde als antwoord.
 */
function rowValueEmpty(value: unknown): boolean {
  if (value === undefined || value === null) return true
  if (typeof value === 'string') return value.trim() === ''
  if (Array.isArray(value)) return value.length === 0
  if (value instanceof Y.Text) return value.toString().trim() === ''
  if (value instanceof Y.XmlFragment || value instanceof Y.Array) return value.length === 0
  return false
}
